/**
 * Elevation Service — USGS Elevation Point Query
 *
 * Fetches elevation data from the free USGS National Map Elevation
 * Point Query Service.  Provides single-point lookups and multi-point
 * profile interpolation for cross-section charts.
 */

const EPQS_BASE =
  'https://epqs.nationalmap.gov/v1/json'

/**
 * Fetch elevation for a single lat/lng coordinate.
 *
 * @param {number} lat - Latitude (decimal degrees)
 * @param {number} lng - Longitude (decimal degrees)
 * @returns {Promise<{elevation: number|null, lat: number, lng: number, error?: string}>}
 */
export async function fetchElevation(lat, lng) {
  try {
    const url = `${EPQS_BASE}?x=${lng}&y=${lat}&wkid=4326&units=Meters&includeDate=false`
    const res = await fetch(url)

    if (!res.ok) {
      return { elevation: null, lat, lng, error: `HTTP ${res.status}` }
    }

    const data = await res.json()
    const elev = parseFloat(data.value)

    if (Number.isNaN(elev) || elev === -1000000) {
      return { elevation: null, lat, lng, error: 'No elevation data at this location' }
    }

    return { elevation: elev, lat, lng }
  } catch (err) {
    return { elevation: null, lat, lng, error: err.message || 'Network error' }
  }
}

/**
 * Interpolate evenly-spaced points along a straight line between two
 * coordinates.  Uses simple linear interpolation (adequate for the
 * distances typical in this application).
 *
 * @param {{lat: number, lng: number}} start
 * @param {{lat: number, lng: number}} end
 * @param {number} count - Total number of points (including start and end)
 * @returns {{lat: number, lng: number}[]}
 */
export function interpolatePoints(start, end, count) {
  if (count < 2) return [{ lat: start.lat, lng: start.lng }]

  const points = []
  for (let i = 0; i < count; i++) {
    const t = i / (count - 1)
    points.push({
      lat: start.lat + t * (end.lat - start.lat),
      lng: start.lng + t * (end.lng - start.lng),
    })
  }
  return points
}

/**
 * Haversine distance between two points in meters.
 * Duplicated locally so this module stays self-contained.
 *
 * @param {number} lat1
 * @param {number} lng1
 * @param {number} lat2
 * @param {number} lng2
 * @returns {number}
 */
function haversine(lat1, lng1, lat2, lng2) {
  const R = 6371000
  const toRad = (d) => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

/**
 * Fetch an elevation profile between two points.
 *
 * Interpolates `numSamples` points along the line, then fetches their
 * elevations in parallel batches of 10 to avoid overwhelming the USGS API.
 *
 * @param {{lat: number, lng: number}} startLatLng
 * @param {{lat: number, lng: number}} endLatLng
 * @param {number} [numSamples=50]
 * @returns {Promise<{distance: number, elevation: number|null, lat: number, lng: number}[]>}
 */
export async function fetchElevationProfile(startLatLng, endLatLng, numSamples = 50) {
  const pts = interpolatePoints(startLatLng, endLatLng, numSamples)
  const BATCH_SIZE = 10
  const results = []

  for (let i = 0; i < pts.length; i += BATCH_SIZE) {
    const batch = pts.slice(i, i + BATCH_SIZE)
    const fetched = await Promise.all(
      batch.map((p) => fetchElevation(p.lat, p.lng))
    )
    results.push(...fetched)
  }

  // Compute cumulative distance from start for each sample
  return results.map((r, idx) => {
    let dist = 0
    if (idx > 0) {
      dist = haversine(pts[0].lat, pts[0].lng, pts[idx].lat, pts[idx].lng)
    }
    return {
      distance: dist,
      elevation: r.elevation,
      lat: r.lat,
      lng: r.lng,
    }
  })
}
